/**
 * Lifecycle API client (A6 删除回收 + 客服代恢复)
 *
 * Stream 2 Phase 2-5b / Plan §10 Stream 2 / Design §5.3 A6.
 *
 * 包装 axios apiClient 调用 admin-service /lifecycle/* 端点；返回类型与 admin-service
 * ServiceResponse 形状对齐。写动作通过 client.ts 自动注入 Idempotency-Key。
 *
 * 覆盖：
 *   A. 客服代恢复 — listRestoreJobs / getRestoreJob / initiateRestoreJob /
 *      approveRestoreJob / rejectRestoreJob / executeRestoreJob
 *   B. 清理监控 — listLifecycleJobs / getLifecycleJob
 *   C. 强制清理（严格限定） — triggerForcePurge / triggerJudicialPurge
 */
import { apiClient } from './client';

// ---------------------------------------------------------------------------
// Types — RestoreJob
// ---------------------------------------------------------------------------

export type RestoreJobStatus =
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'executed'
  | 'failed';

export interface RestoreJobItem {
  id: string;
  workId: string;
  userId: string;
  userRequestText: string;
  status: RestoreJobStatus;
  initiatorId: string;
  initiatedAt: string;
  approverId: string | null;
  approvedAt: string | null;
  executorId: string | null;
  executedAt: string | null;
  rejectReason: string | null;
  executionFailureReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RestoreJobDetail extends RestoreJobItem {
  approvalNote?: string | null;
  executionNote?: string | null;
}

// ---------------------------------------------------------------------------
// Types — LifecycleJob
// ---------------------------------------------------------------------------

export type LifecycleJobType =
  | 'force_purge_recycled'
  | 'judicial_purge'
  | 'lifecycle_recycle'
  | 'lifecycle_purge';

export type LifecycleJobStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed';

export interface LifecycleJobItem {
  jobId: string;
  jobType: LifecycleJobType;
  targetWorkId: string;
  status: LifecycleJobStatus;
  evidenceUrl: string | null;
  errorMessage: string | null;
  createdBy: string;
  createdAt: string;
  executedAt: string | null;
  failedAt: string | null;
}

// ---------------------------------------------------------------------------
// Types — List responses
// ---------------------------------------------------------------------------

export interface ListRestoreJobsResponse {
  items: RestoreJobItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ListLifecycleJobsResponse {
  items: LifecycleJobItem[];
  total: number;
  page: number;
  pageSize: number;
}

// ---------------------------------------------------------------------------
// Types — Filter / Input
// ---------------------------------------------------------------------------

export interface ListRestoreJobsFilter {
  status?: RestoreJobStatus;
  initiatorId?: string;
  workId?: string;
  userId?: string;
  fromDate?: string; // ISO
  toDate?: string;   // ISO
  page?: number;
  pageSize?: number;
}

export interface ListLifecycleJobsFilter {
  jobType?: LifecycleJobType | 'all';
  status?: LifecycleJobStatus;
  workId?: string;
  createdBy?: string;
  fromDate?: string; // ISO
  toDate?: string;   // ISO
  page?: number;
  pageSize?: number;
}

export interface InitiateRestoreJobInput {
  workId: string;
  userId: string;
  /** 必须粘贴用户原始请求文字，20-1000 chars */
  userRequestText: string;
}

export interface ApproveRestoreJobInput {
  approvalNote?: string;
}

export interface RejectRestoreJobInput {
  /** 5+ chars */
  rejectReason: string;
}

export interface ExecuteRestoreJobInput {
  executionNote?: string;
}

export interface ForcePurgeInput {
  /** 1-100 workIds */
  targetWorkIds: string[];
  /** 5-200 chars */
  reason: string;
}

export interface JudicialPurgeInput {
  /** 1-100 workIds */
  targetWorkIds: string[];
  /** must start with https:// */
  evidenceUrl: string;
  /** 5-200 chars */
  reason: string;
  requestType: 'court_order' | 'police_request' | 'compliance';
}

export interface PurgeResult {
  jobIds: string[];
}

// ---------------------------------------------------------------------------
// Internal ServiceResponse wrapper
// ---------------------------------------------------------------------------

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

// ---------------------------------------------------------------------------
// A. 客服代恢复
// ---------------------------------------------------------------------------

/** GET /lifecycle/restore-jobs — 代恢复任务列表（分页 + 多字段过滤）。 */
export async function listRestoreJobs(
  filter: ListRestoreJobsFilter = {},
): Promise<ListRestoreJobsResponse> {
  const params: Record<string, string | number> = {};
  if (filter.status) params.status = filter.status;
  if (filter.initiatorId) params.initiatorId = filter.initiatorId;
  if (filter.workId) params.workId = filter.workId;
  if (filter.userId) params.userId = filter.userId;
  if (filter.fromDate) params.fromDate = filter.fromDate;
  if (filter.toDate) params.toDate = filter.toDate;
  if (filter.page != null) params.page = filter.page;
  if (filter.pageSize != null) params.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<ListRestoreJobsResponse>>(
    '/lifecycle/restore-jobs',
    { params },
  );
  return resp.data.data;
}

/** GET /lifecycle/restore-jobs/:id — 代恢复任务详情（含 approvalNote）。 */
export async function getRestoreJob(id: string): Promise<RestoreJobDetail> {
  const resp = await apiClient.get<ServiceResponse<RestoreJobDetail>>(
    `/lifecycle/restore-jobs/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** POST /lifecycle/restore-jobs — 发起客服代恢复（USER_RESTORE）。 */
export async function initiateRestoreJob(input: InitiateRestoreJobInput): Promise<void> {
  await apiClient.post<ServiceResponse<null>>('/lifecycle/restore-jobs', input);
}

/** POST /lifecycle/restore-jobs/:id/approve — 审批通过（USER_RESTORE）。 */
export async function approveRestoreJob(
  id: string,
  input: ApproveRestoreJobInput = {},
): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/lifecycle/restore-jobs/${encodeURIComponent(id)}/approve`,
    input,
  );
}

/** POST /lifecycle/restore-jobs/:id/reject — 拒绝代恢复（USER_RESTORE）。 */
export async function rejectRestoreJob(
  id: string,
  input: RejectRestoreJobInput,
): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/lifecycle/restore-jobs/${encodeURIComponent(id)}/reject`,
    input,
  );
}

/** POST /lifecycle/restore-jobs/:id/execute — 执行代恢复（USER_RESTORE）。 */
export async function executeRestoreJob(
  id: string,
  input: ExecuteRestoreJobInput = {},
): Promise<void> {
  await apiClient.post<ServiceResponse<null>>(
    `/lifecycle/restore-jobs/${encodeURIComponent(id)}/execute`,
    input,
  );
}

// ---------------------------------------------------------------------------
// B. 清理监控
// ---------------------------------------------------------------------------

/** GET /lifecycle/lifecycle-jobs — 清理任务监控列表（分页 + 多字段过滤）。 */
export async function listLifecycleJobs(
  filter: ListLifecycleJobsFilter = {},
): Promise<ListLifecycleJobsResponse> {
  const params: Record<string, string | number> = {};
  if (filter.jobType && filter.jobType !== 'all') params.jobType = filter.jobType;
  if (filter.status) params.status = filter.status;
  if (filter.workId) params.workId = filter.workId;
  if (filter.createdBy) params.createdBy = filter.createdBy;
  if (filter.fromDate) params.fromDate = filter.fromDate;
  if (filter.toDate) params.toDate = filter.toDate;
  if (filter.page != null) params.page = filter.page;
  if (filter.pageSize != null) params.pageSize = filter.pageSize;

  const resp = await apiClient.get<ServiceResponse<ListLifecycleJobsResponse>>(
    '/lifecycle/lifecycle-jobs',
    { params },
  );
  return resp.data.data;
}

/** GET /lifecycle/lifecycle-jobs/:id — 清理任务详情。 */
export async function getLifecycleJob(id: string): Promise<LifecycleJobItem> {
  const resp = await apiClient.get<ServiceResponse<LifecycleJobItem>>(
    `/lifecycle/lifecycle-jobs/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

// ---------------------------------------------------------------------------
// C. 强制清理（严格限定）
// ---------------------------------------------------------------------------

/**
 * POST /lifecycle/lifecycle-force-purge — 兜底强制清理（USER_FREEZE）。
 * 仅限 work 已过 30 天 + 在回收期；后端逐个校验。
 */
export async function triggerForcePurge(input: ForcePurgeInput): Promise<PurgeResult> {
  const resp = await apiClient.post<ServiceResponse<PurgeResult>>(
    '/lifecycle/lifecycle-force-purge',
    input,
  );
  return resp.data.data;
}

/**
 * POST /lifecycle/lifecycle-judicial-purge — 司法/合规清理（LIFECYCLE_FORCE_PURGE_JUDICIAL）。
 * 要求三人审批（含法务）+ 上传依据 URL。
 */
export async function triggerJudicialPurge(input: JudicialPurgeInput): Promise<PurgeResult> {
  const resp = await apiClient.post<ServiceResponse<PurgeResult>>(
    '/lifecycle/lifecycle-judicial-purge',
    input,
  );
  return resp.data.data;
}
